import { ISLANDS_TO_FARM } from 'constants/lists'
import { useConnection } from 'hooks/useConnection'
import { useIslandById } from 'hooks/useIslandsApi'
import { useKodiakFarm } from 'hooks/useKodiakFarm'
import { useRewardVaultBasicInfo } from 'hooks/useRewardVault'
// import { useActiveWeb3React } from 'hooks/web3'
import { ReactNode, useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { IslandApiResponse } from 'state/data/slice'
import { isAddress } from 'viem'

// Define types for the route props
export interface PoolRouteProps {
  tokenId?: string
  hasKodiakFarm?: boolean
  kodiakFarmAddress?: string
  rewardVaultAddress?: string
  originalKodiakFarmAddress?: string
  isWhitelistedForRewardVault?: boolean
  isRewardVaultView: boolean
  poolApiResponse?: IslandApiResponse
  isLoading?: boolean
}

interface PoolRouteHandlerProps {
  children: (props: PoolRouteProps) => ReactNode
}

export function PoolRouteHandler({ children }: PoolRouteHandlerProps): JSX.Element {
  const { tokenId: rawTokenId, farm: farmAddressParam } = useParams<{ tokenId?: string; farm?: string }>()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const { chainId } = useConnection()

  const validTokenId = useMemo(() => {
    if (!rawTokenId || typeof rawTokenId !== 'string') return undefined
    return isAddress(rawTokenId) ? rawTokenId : undefined
  }, [rawTokenId])

  const farmParam = farmAddressParam || searchParams.get('farm') || undefined

  const validFarmParam = useMemo(() => {
    return farmParam && isAddress(farmParam) ? farmParam : undefined
  }, [farmParam])

  const { data: islandData, isLoading } = useIslandById(validTokenId)

  const poolApiResponse = useMemo(() => {
    if (isLoading) {
      return undefined
    }
    return islandData || undefined
  }, [isLoading, islandData])

  const isWhitelistedForRewardVault = useMemo(() => {
    if (isLoading) {
      return false
    }
    return poolApiResponse?.isRewardVault ?? false
  }, [isLoading, poolApiResponse])

  const rewardVaultAddress = useMemo(() => {
    if (isLoading || !poolApiResponse?.isRewardVault) return undefined
    const addr = poolApiResponse?.farm?.id
    return addr && isAddress(addr) ? addr : undefined
  }, [isLoading, poolApiResponse])

  const originalKodiakFarmAddress = useMemo(() => {
    if (!chainId || !validTokenId) return undefined
    const chainMapping = ISLANDS_TO_FARM[chainId.toString()]
    if (!chainMapping) return undefined
    return chainMapping[validTokenId.toLowerCase()]
  }, [chainId, validTokenId])

  const isFarmParamRewardVault = useMemo(() => {
    if (isLoading) return false
    return validFarmParam === rewardVaultAddress
  }, [isLoading, validFarmParam, rewardVaultAddress])

  // Determine if we should show the vault view:
  // 1. Island is whitelisted AND either:
  //    a. No farm parameter is specified (showing vault by default for whitelisted islands), OR
  //    b. The farm parameter is actually the reward vault address
  const isRewardVaultView = useMemo(() => {
    if (isLoading) {
      return false
    }
    return isWhitelistedForRewardVault && (!validFarmParam || isFarmParamRewardVault)
  }, [isLoading, isWhitelistedForRewardVault, validFarmParam, isFarmParamRewardVault])

  const hasKodiakFarm = useMemo(() => {
    const hasCurrentKodiakFarm = !isRewardVaultView && !!validFarmParam
    return hasCurrentKodiakFarm || !!originalKodiakFarmAddress
  }, [isRewardVaultView, validFarmParam, originalKodiakFarmAddress])

  const props: PoolRouteProps = {
    tokenId: rawTokenId,
    hasKodiakFarm,
    // Only pass farmAddress if not showing vault view
    kodiakFarmAddress: isRewardVaultView ? undefined : validFarmParam,
    // Only pass vaultAddress if showing vault view
    rewardVaultAddress: isRewardVaultView ? rewardVaultAddress : undefined,
    // Always pass the original farm address so PoolPage can check for positions
    originalKodiakFarmAddress: isWhitelistedForRewardVault ? originalKodiakFarmAddress : undefined,
    isWhitelistedForRewardVault,
    isRewardVaultView,
    poolApiResponse,
    isLoading,
  }

  interface PoolPageProps {
    tokenId?: string
    hasKodiakFarm?: boolean
    kodiakFarmAddress?: string
    rewardVaultAddress?: string
    originalKodiakFarmAddress?: string
    isWhitelistedForRewardVault?: boolean
    isRewardVaultView: boolean
    poolApiResponse?: IslandApiResponse
    isLoading?: boolean
  }

  return <>{children(props)}</>
}

export interface GenericFarmRouteProps {
  farmAddress?: string
  vaultAddress?: string
  originalFarmAddress?: string
  isWhitelisted?: boolean
  isVaultView: boolean
}

interface GenericFarmRouteHandlerProps {
  children: (props: GenericFarmRouteProps) => ReactNode
}

export function GenericFarmRouteHandler({ children }: GenericFarmRouteHandlerProps): JSX.Element {
  const { farmAddress } = useParams<{ farmAddress?: string }>()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)

  // Get farm parameter from URL params or query
  const addressParam = farmAddress || ''

  // Check for explicit vault parameter in query string
  const vaultParam = searchParams.get('vault') || undefined

  // Try to get basic vault info to determine if this is a vault
  const { error: vaultError, data: vaultData } = useRewardVaultBasicInfo(addressParam)

  // Try to get farm data to determine if this is a farm
  const { isError: isFarmError, data: farmData } = useKodiakFarm(addressParam)

  // Determine if address is a vault or farm
  const isVault = vaultParam || (!vaultError && vaultData?.stakeToken)
  // const isFarm = !isVault && !isFarmError && farmData

  // Flag as vault view if explicit vault param or detected as vault
  const isVaultView = !!isVault

  // If we're viewing a vault, original farm might be the same address
  // This is used for migration scenarios
  const originalFarmAddress = isVaultView && farmData ? addressParam : undefined

  // Consider "whitelisted" if we have both farm and vault at same address
  const isWhitelisted = !!originalFarmAddress

  const props: GenericFarmRouteProps = {
    // Only pass farmAddress if viewing farm
    farmAddress: !isVaultView ? addressParam : undefined,

    // Only pass vaultAddress if viewing vault
    vaultAddress: isVaultView ? vaultParam || addressParam : undefined,

    // For migration purposes
    originalFarmAddress,

    isWhitelisted,
    isVaultView,
  }

  return <>{children(props)}</>
}
